import { useEffect, useState } from "react";
import portalApi from "../../api/portalClient";
import { useBranding } from "../../context/BrandingContext";
import { formatDateTime } from "../../utils/datetime";

/**
 * Portal → Activity: what has been done to this customer's environment.
 *
 * The staff audit screen is admin-only and refuses this outright, correctly —
 * the raw table can name which credential was revealed. What a client can see
 * is decided server-side in services/portal_audit.py: their own customer only,
 * a default-deny allow-list of action families, and never the detail payload.
 * Nothing here re-implements that; the browser only renders what it is given.
 */

type Entry = {
  id: number;
  created_at: string;
  action: string;
  label: string;
  actor: string;
  target_type: string | null;
  target_label: string | null;
  result: string;
};

const RESULT_STYLE: Record<string, string> = {
  success: "bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300",
  failed: "bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300",
  pending: "bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300",
};

export default function PortalAudit() {
  const { display_timezone: tz } = useBranding();
  const [entries, setEntries] = useState<Entry[] | null>(null);
  const [actions, setActions] = useState<string[]>([]);
  const [action, setAction] = useState("");
  const [days, setDays] = useState(30);
  const [truncated, setTruncated] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    setEntries(null);
    setError("");
    portalApi
      .get("/api/v1/portal/audit", { params: { days, ...(action ? { action } : {}) } })
      .then((r) => {
        setEntries(r.data.entries || []);
        setActions(r.data.actions || []);
        setTruncated(!!r.data.truncated);
      })
      .catch((e: any) => {
        setEntries([]);
        setError(e?.response?.data?.detail || "Could not load the activity log");
      });
  }, [days, action]);

  return (
    <div className="max-w-5xl">
      <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-1">Activity</h1>
      <p className="text-sm text-gray-500 dark:text-gray-400 mb-5">
        Every recorded change to your environment — what was done, to what, by whom, and when.
        Includes remote sessions onto your machines.
      </p>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <select
          value={days}
          onChange={(e) => setDays(Number(e.target.value))}
          className="border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-3 py-2 text-sm"
        >
          <option value={7}>Last 7 days</option>
          <option value={30}>Last 30 days</option>
          <option value={90}>Last 90 days</option>
          <option value={365}>Last year</option>
        </select>
        <select
          value={action}
          onChange={(e) => setAction(e.target.value)}
          className="border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-3 py-2 text-sm max-w-xs"
        >
          <option value="">All activity</option>
          {actions.map((a) => (
            <option key={a} value={a}>
              {a.replace(/_/g, " ")}
            </option>
          ))}
        </select>
        {entries && (
          <span className="text-xs text-gray-500 dark:text-gray-400">
            {entries.length} {entries.length === 1 ? "entry" : "entries"}
            {/* Never let a cap read as "that's everything". */}
            {truncated ? " (most recent shown — narrow the range for more)" : ""}
          </span>
        )}
      </div>

      {error && (
        <p className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/30 px-3 py-2 rounded-lg mb-4">
          {error}
        </p>
      )}

      {entries === null ? (
        <p className="text-sm text-gray-400">Loading…</p>
      ) : entries.length === 0 ? (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 p-8 text-center">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Nothing recorded in this period.
          </p>
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-900/50 border-b dark:border-gray-700">
              <tr>
                {["When", "What", "Target", "Who", "Result"].map((h) => (
                  <th
                    key={h}
                    className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {entries.map((e) => (
                <tr key={e.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/40">
                  {/* API sends zone-less UTC — formatDateTime, never new Date(). */}
                  <td className="px-4 py-3 whitespace-nowrap text-gray-600 dark:text-gray-300">
                    {formatDateTime(e.created_at, tz)}
                  </td>
                  <td className="px-4 py-3 text-gray-800 dark:text-gray-100">{e.label}</td>
                  <td className="px-4 py-3 text-gray-600 dark:text-gray-300">
                    {e.target_label || "—"}
                    {e.target_type && (
                      <span className="block text-[11px] text-gray-400 dark:text-gray-500">
                        {e.target_type.replace(/_/g, " ")}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-gray-600 dark:text-gray-300">{e.actor}</td>
                  <td className="px-4 py-3">
                    <span
                      className={`text-xs px-2 py-0.5 rounded-full ${
                        RESULT_STYLE[e.result] ||
                        "bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300"
                      }`}
                    >
                      {e.result}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
